/**
 * Client-side email format feedback for native Liquid forms (email-signup, contact-form).
 *
 * The browser's type="email" and Shopify's server validation both accept TLD-less
 * addresses like "test@example", so an obviously-incomplete email submits without any
 * feedback. The actual submission is now blocked natively by a `pattern` attribute on the
 * input (see the markup) — that's reliable regardless of when/if this script loads. This
 * element only upgrades the experience: it replaces the browser's default validation
 * tooltip with the inline, styled error message and flags the field (red border via
 * aria-invalid).
 *
 * Wrap a single <form> with one email input:
 *   <email-form-validation>
 *     {% form ... %} ... <input type="email" pattern="..."> ... [data-email-error] ... {% endform %}
 *   </email-form-validation>
 */

class EmailFormValidation extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #message = null;

  connectedCallback() {
    this.#input = this.querySelector('input[type="email"]');
    this.#message = this.querySelector('[data-email-error]');
    if (!this.#input) return;

    // `invalid` fires on the field when the browser blocks submission via the constraint.
    this.#input.addEventListener('invalid', this.#onInvalid);
    this.#input.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('invalid', this.#onInvalid);
    this.#input?.removeEventListener('input', this.#onInput);
  }

  /** @param {Event} event */
  #onInvalid = (event) => {
    // Suppress the native tooltip; the browser still refuses to submit while invalid.
    event.preventDefault();
    this.#setInvalid(true);
    this.#input?.focus();
  };

  // Clear the styled error once the value satisfies the field's constraints again.
  #onInput = () => {
    if (this.#input?.validity.valid) this.#setInvalid(false);
  };

  /** @param {boolean} invalid */
  #setInvalid(invalid) {
    const input = this.#input;
    const message = this.#message;
    if (!input) return;

    input.setAttribute('aria-invalid', String(invalid));

    if (!message) return;
    message.hidden = !invalid;

    const describedBy = (input.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter((id) => id && id !== message.id);
    if (invalid) describedBy.push(message.id);
    if (describedBy.length) {
      input.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      input.removeAttribute('aria-describedby');
    }
  }
}

if (!customElements.get('email-form-validation')) {
  customElements.define('email-form-validation', EmailFormValidation);
}
